import { HAZARD_CATEGORIES } from "encamp-shared/src/hazards/hazardCategories.generated";
import { ChemicalInput } from "generated-graphql/graphql";
import { HealthHazardOrder, PhysicalHazardOrder } from "util/hazards";

import { labelsInCheckboxOrder, legacyHazardsForCodes } from "../Chemical/hazardCategoryFormValues";

type MergeHazardFields = Pick<
  ChemicalInput,
  "hazardCategoryCodes" | "physicalHazards" | "healthHazards"
>;

/** The merge field whose picked candidate supplies the merged chemical's hazards. */
export const HAZARDS_FIELD_NAME = "hazardCategories";

/** Candidate codes, undefined when the chemical has none, so it merges by its legacy arrays. */
export function candidateHazardCategoryCodes(
  hazardCategories: readonly { code: string }[]
): string[] | undefined {
  return hazardCategories.length > 0 ? hazardCategories.map((c) => c.code) : undefined;
}

/**
 * The picked source's hazards: its categories with the legacy arrays derived from
 * them, or its legacy arrays alone when it has no categories.
 */
export function mergedHazardFields(selection: MergeHazardFields): MergeHazardFields {
  const codes = selection.hazardCategoryCodes;
  if (!codes) {
    return {
      hazardCategoryCodes: undefined,
      physicalHazards: selection.physicalHazards,
      healthHazards: selection.healthHazards,
    };
  }
  const derived = legacyHazardsForCodes(codes);
  const physical = new Set<string>(derived.physicalHazards);
  const health = new Set<string>(derived.healthHazards);
  return {
    hazardCategoryCodes: codes,
    physicalHazards: PhysicalHazardOrder.filter((hazard) => physical.has(hazard)),
    healthHazards: HealthHazardOrder.filter((hazard) => health.has(hazard)),
  };
}

export function hasMergeHazards(input: MergeHazardFields): boolean {
  return (
    (input.hazardCategoryCodes?.length ?? 0) > 0 ||
    (input.physicalHazards?.length ?? 0) > 0 ||
    (input.healthHazards?.length ?? 0) > 0
  );
}

/** Category labels in catalog order; a code with no label shows as itself rather than blank. */
function categoryLabels(codes: readonly string[]): string[] {
  const known = HAZARD_CATEGORIES.filter((row) => codes.includes(row.code));
  const knownCodes = new Set<string>(known.map((row) => row.code));
  return [...known.map((row) => row.label), ...codes.filter((code) => !knownCodes.has(code))];
}

/** One "- label" line per category, or per legacy hazard when there are none. */
export function renderMergeHazards(data: MergeHazardFields): string {
  const codes = data.hazardCategoryCodes;
  let labels: string[];
  if (codes) {
    labels = categoryLabels(codes);
  } else {
    const legacy = labelsInCheckboxOrder(data.physicalHazards ?? [], data.healthHazards ?? []);
    labels = [...legacy.physical, ...legacy.health];
  }
  return labels.map((label) => `- ${label}`).join("\n");
}
